/* LUGAZI ALIVE — interactive layer (v8)
   Runs after lugazi-world.js. Does not modify that file.
   BOOK: enhances the collage that lugazi-world.js has built:
   - the collage scrolls down as before, but a few "chapters" pin and
     slide sideways while you keep scrolling
   - pages are drawn in when they appear, lift on hover, open on click
   EVERY PAGE: the cat washes, scratches, stretches, sleeps, purrs and
   startles; nav icons wobble;
   clicks leave an ink splat. HOME: the face's eyes blink; the tree face
   grows, floats and drifts in layers; the third figure breathes, smokes,
   twitches, turns toward you and its crosses stitch in and fly.
   v9: works with touch too (eyes, tree, figure, book, cat, nav), a new
   pointer trail made of motifs from the drawings, and the logo lands at
   the bottom of BOOK like on the home page. */
(() => {
  'use strict';

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const ARROW = 'https://lugaziworld.com/cdn/shop/files/lugazi-arrow.png?v=15856676952099271157';
  // Spreads (0-based, in collage order) that become sideways chapters
  // v11: only one sideways chapter, the rest is a still collage
  const CHAPTERS = [[15, 17]];
  // v10: inside a chapter, 1 px of scroll moves the pages 1 / RAIL_SPEED px
  // sideways, so the chapters take less scrolling
  const RAIL_SPEED = 0.45;
  // Hand-picked details that come alive: x/y = centre (% of the page),
  // r = radius (% of page width), fx = animation (see lugazi-alive.css)
  const DETAILS = {
    3732: [{ x: 41, y: 42, r: 9, fx: 'twist' }],
    3739: [{ x: 13, y: 15, r: 11, fx: 'twist' }],
    3754: [{ x: 50, y: 43, r: 8, fx: 'twist' }],
    3816: [{ x: 31, y: 20, r: 8, fx: 'pulse' }, { x: 22, y: 20, r: 3.5, fx: 'twist' }, { x: 59, y: 13, r: 3.5, fx: 'twist' }],
    3773: [{ x: 40, y: 44, r: 19, fx: 'pulse' }, { x: 25, y: 80, r: 6, fx: 'twist' }],
    3755: [{ x: 71, y: 24, r: 8, fx: 'twinkle' }, { x: 15, y: 39, r: 7, fx: 'twinkle' }],
    3790: [{ x: 33, y: 24, r: 17, fx: 'sway' }, { x: 21, y: 57, r: 15, fx: 'sway' }, { x: 74, y: 75, r: 13, fx: 'sway' }],
    4547: [{ x: 87, y: 30, r: 7, fx: 'twist' }],
  };
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const rand = (seed) => {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  };
  const touch = !fine.matches;
  // one place for "where is the pointer / finger": mouse moves, and touches
  // (touchmove keeps firing while the page scrolls, pointermove does not)
  function onPoint(fn) {
    addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') fn(e.clientX, e.clientY, 'mouse'); }, { passive: true });
    const t = (e) => { const p = e.touches[0]; if (p) fn(p.clientX, p.clientY, 'touch'); };
    addEventListener('touchstart', t, { passive: true });
    addEventListener('touchmove', t, { passive: true });
  }

  function initBook() {
    const book = document.querySelector('.lw-book');
    const collage = document.querySelector('.lw-book-collage');
    const wrapper = document.querySelector('.page-wrapper');
    if (!book || !collage || book.hidden || book.dataset.lwa) return;
    if (reduced.matches) return; // keep the original collage untouched
    book.dataset.lwa = '1';
    document.body.classList.add('lwa-book');

    // Desktop: .page-wrapper scrolls. Mobile: the document scrolls.
    const findScroller = () =>
      wrapper && /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY)
        ? wrapper
        : document.scrollingElement;
    let scroller = findScroller();
    const scrollTopOf = (el) => {
      const top0 = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top;
      return el.getBoundingClientRect().top - top0 + scroller.scrollTop;
    };

    // Shared SVG filter: soft "boiling ink" on the lifted page
    const ns = 'http://www.w3.org/2000/svg';
    const defs = document.createElementNS(ns, 'svg');
    defs.setAttribute('aria-hidden', 'true');
    defs.style.cssText = 'position:absolute;width:0;height:0';
    defs.innerHTML =
      '<filter id="lwa-boil" x="-3%" y="-3%" width="106%" height="106%">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" seed="1"/>' +
      '<feDisplacementMap in="SourceGraphic" scale="2.5" xChannelSelector="R" yChannelSelector="G"/>' +
      '</filter>' +
      // "Wet ink": glints of light run along the blue/green marker strokes
      // and the strokes ripple slightly; black ink stays completely still
      '<filter id="lwa-wet" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">' +
      '<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -3 0 3 0 -0.15" result="blue"/>' +
      '<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -3 3 0 0 -0.15" result="green"/>' +
      '<feMerge result="mask"><feMergeNode in="blue"/><feMergeNode in="green"/></feMerge>' +
      '<feGaussianBlur in="mask" stdDeviation="2.5" result="soft"/>' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.006 0.022" numOctaves="2" seed="7" result="noise"/>' +
      '<feOffset in="noise" dx="0" dy="0" result="flow"/>' +
      // ripple: move only the coloured strokes
      '<feComposite in="flow" in2="soft" operator="in" result="flowMask"/>' +
      '<feFlood flood-color="rgb(128,128,128)" result="neutral"/>' +
      '<feMerge result="map"><feMergeNode in="neutral"/><feMergeNode in="flowMask"/></feMerge>' +
      '<feDisplacementMap in="SourceGraphic" in2="map" scale="7" xChannelSelector="R" yChannelSelector="G" result="rippled"/>' +
      // glints: thin bright bands of the noise, kept inside the strokes
      '<feColorMatrix in="flow" type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 1 0 0 0 0" result="lum"/>' +
      '<feComponentTransfer in="lum" result="bands"><feFuncA type="table" tableValues="0 0 0 0 .75 0 0 0 0"/></feComponentTransfer>' +
      '<feComposite in="bands" in2="mask" operator="in" result="glint"/>' +
      '<feMerge><feMergeNode in="rippled"/><feMergeNode in="glint"/></feMerge>' +
      '</filter>';
    document.body.append(defs);
    const turbulence = defs.querySelector('#lwa-boil feTurbulence');
    const wetFlow = defs.querySelector('#lwa-wet feOffset');

    /* --- every page gets a paper wrapper ----------------------------- */
    const spreads = [...collage.querySelectorAll(':scope > .lw-collage-spread')];
    const pages = [...collage.querySelectorAll('.lw-collage-page')];
    pages.forEach((page, i) => {
      const paper = document.createElement('span');
      paper.className = 'lwa-paper';
      while (page.firstChild) paper.append(page.firstChild);
      page.append(paper);
      page.lwa = { i, paper, rail: null };
    });

    /* --- sideways chapters ("rails") --------------------------------- */
    const rails = CHAPTERS.map(([a, b], n) => {
      const group = spreads.slice(a, b + 1);
      if (group.length < 2) return null;
      const rail = document.createElement('section');
      rail.className = 'lwa-rail';
      rail.setAttribute('aria-label', `BOOK – kapitel ${n + 1}`);
      const stage = document.createElement('div');
      stage.className = 'lwa-stage';
      const track = document.createElement('div');
      track.className = 'lwa-track';
      group[0].before(rail);
      rail.append(stage);
      stage.append(track);
      group.forEach((s) => track.append(s));
      const railPages = [...track.querySelectorAll('.lw-collage-page')];
      railPages.forEach((page, k) => {
        const L = page.lwa;
        // keep the page's aspect ratio even where old CSS forces "auto"
        const ar = page.style.aspectRatio;
        if (ar) page.style.setProperty('aspect-ratio', ar, 'important');
        const cutout = page.classList.contains('lw-cutout-page');
        const tall = parseFloat(page.style.getPropertyValue('--page-ratio') || '0.75') < 0.8;
        const h = cutout ? (tall ? 70 : 62) : 62; // v11: objects take more room
        page.style.setProperty('--lwa-h', `min(${h}svh, ${Math.round(h * 1.3)}vw)`);
        L.rail = null; // set below
        L.baseY = (rand(L.i) - 0.5) * 8;          // svh
        L.baseR = (rand(L.i + 50) - 0.5) * 4;     // deg
        L.depth = 0.85 + rand(L.i + 99) * 0.3;
        L.left = 0;
        L.width = 0;
        L.k = k;
      });
      const R = { el: rail, stage, track, pages: railPages, x: 0, travel: 0, top: 0, active: false };
      railPages.forEach((p) => { p.lwa.rail = R; });
      return R;
    }).filter(Boolean);

    let vw = 0, vh = 0;
    function layout() {
      vw = innerWidth;
      vh = innerHeight;
      scroller = findScroller();
      for (const R of rails) {
        R.travel = Math.max(0, R.track.scrollWidth - vw);
        R.el.style.height = `${R.travel * RAIL_SPEED + vh}px`;
      }
      // measure tops after all heights are set
      for (const R of rails) {
        R.top = scrollTopOf(R.el);
        for (const p of R.pages) {
          p.lwa.left = p.offsetLeft; // relative to the sticky stage
          p.lwa.width = p.offsetWidth;
        }
      }
    }
    layout();
    addEventListener('resize', layout);
    [300, 1200, 3000].forEach((t) => setTimeout(layout, t));

    /* --- progress line: how far through the whole BOOK (v10) ---------- */
    const progress = document.createElement('div');
    progress.className = 'lwa-progress';
    progress.innerHTML = '<i></i><span class="lwa-count"></span>';
    const count = progress.querySelector('.lwa-count');
    const hint = document.createElement('div');
    hint.className = 'lwa-hint';
    hint.innerHTML = `<span>SCROLL</span><img src="${ARROW}" alt="">`;
    document.body.append(progress, hint);

    /* --- sideways trackpad swipe also moves a chapter ----------------- */
    addEventListener('wheel', (e) => {
      if (box.isOpen) return;
      if (!rails.some((R) => R.active)) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        scroller.scrollTop += e.deltaX * RAIL_SPEED;
        e.preventDefault();
      }
    }, { passive: false });

    /* --- living details: a feathered copy of one spot, animated ------- */
    function addDetails(page) {
      const list = DETAILS[page.dataset.bookId];
      if (!list || page.lwa.details) return;
      page.lwa.details = true;
      const pending = page.querySelector('image[data-source]');
      if (pending) { pending.setAttribute('href', pending.dataset.source); delete pending.dataset.source; }
      const source = page.lwa.paper.firstElementChild;
      const ratio = page.offsetWidth / page.offsetHeight || 0.75;
      list.forEach((d, n) => {
        const layer = document.createElement('span');
        layer.className = `lwa-detail lwa-fx-${d.fx}`;
        layer.setAttribute('aria-hidden', 'true');
        const rx = d.r, ry = d.r * ratio;
        const mask = `radial-gradient(ellipse ${rx}% ${ry}% at ${d.x}% ${d.y}%, #000 62%, transparent 100%)`;
        layer.style.cssText =
          `transform-origin:${d.x}% ${d.y}%;-webkit-mask-image:${mask};mask-image:${mask};` +
          `animation-delay:${-(rand(page.lwa.i + n * 7) * 6).toFixed(2)}s`;
        const copy = source.cloneNode(true);
        if (copy.tagName === 'IMG') copy.loading = 'eager';
        layer.append(copy);
        page.lwa.paper.append(layer);
      });
    }

    /* --- reveal: every page is drawn in the first time it shows ------- */
    const seen = new IntersectionObserver((entries) => {
      let n = 0;
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        en.target.style.setProperty('--lwa-delay', `${(n++ % 3) * 0.12}s`);
        addDetails(en.target);
        en.target.classList.add('is-in');
        seen.unobserve(en.target);
      }
    }, { threshold: 0.12 });
    pages.forEach((p) => seen.observe(p));

    /* --- preload pages inside chapters before they slide in ----------- */
    function preload(R) {
      for (const p of R.pages) {
        if (p.lwa.loaded) continue;
        const d = p.lwa.left - R.x;
        if (d < vw * 2.2) {
          const svgImg = p.querySelector('image[data-source]');
          if (svgImg) { svgImg.setAttribute('href', svgImg.dataset.source); delete svgImg.dataset.source; }
          const img = p.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
          p.lwa.loaded = true;
        }
      }
    }

    /* --- hover: lift + tilt + sheen + soft boiling ink ---------------- */
    let lifted = null;
    if (fine.matches) {
      collage.addEventListener('pointermove', (e) => {
        const page = e.target.closest('.lw-collage-page');
        if (page !== lifted) {
          if (lifted) drop(lifted);
          lifted = page;
          if (page) page.classList.add('is-lifted');
        }
        if (!page) return;
        const r = page.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        // pages from the notebook tilt like paper; objects just lift
        page.lwa.paper.style.transform = page.classList.contains('lw-cutout-page')
          ? `translateY(-10px) rotate(${(nx * 5).toFixed(2)}deg) scale(1.05)`
          : `perspective(1100px) rotateY(${nx * 12}deg) rotateX(${-ny * 9}deg) scale(1.04)`;
        page.lwa.paper.style.setProperty('--mx', `${(nx + 0.5) * 100}%`);
        page.lwa.paper.style.setProperty('--my', `${(ny + 0.5) * 100}%`);
      });
      collage.addEventListener('pointerleave', () => { if (lifted) drop(lifted); lifted = null; });
    } else {
      // touch: the page lifts and tilts under the finger while pressed
      collage.addEventListener('pointerdown', (e) => {
        const page = e.target.closest('.lw-collage-page');
        if (!page) return;
        if (lifted && lifted !== page) drop(lifted);
        lifted = page;
        page.classList.add('is-lifted');
        const r = page.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        page.lwa.paper.style.transform = page.classList.contains('lw-cutout-page')
          ? `translateY(-8px) rotate(${(nx * 4).toFixed(2)}deg) scale(1.04)`
          : `perspective(1100px) rotateY(${nx * 10}deg) rotateX(${-ny * 8}deg) scale(1.03)`;
      }, { passive: true });
      const release = () => { if (lifted) { const p = lifted; lifted = null; setTimeout(() => drop(p), 120); } };
      addEventListener('pointerup', release, { passive: true });
      addEventListener('pointercancel', release, { passive: true });
    }
    function drop(page) {
      page.classList.remove('is-lifted');
      page.lwa.paper.style.transform = '';
    }

    /* --- main loop ---------------------------------------------------- */
    let lastBoil = 0, lastWet = 0, lastPick = 0, lastCount = 0, wetPage = null;
    function frame(t) {
      const st = scroller.scrollTop;
      let shown = null;
      for (const R of rails) {
        const raw = (st - R.top) / RAIL_SPEED;
        R.active = raw > -vh * 0.15 && raw < R.travel + vh * 0.15;
        const near = raw > -vh * 1.5 && raw < R.travel + vh * 1.5;
        if (!near) continue;
        const target = clamp(raw, 0, R.travel);
        const prev = R.x;
        R.x += (target - R.x) * 0.12;
        if (Math.abs(target - R.x) < 0.1) R.x = target;
        const v = R.x - prev;
        R.track.style.transform = `translate3d(${-R.x}px,0,0)`;
        const flutter = clamp(v * 0.025, -2, 2);
        let nearest = 0, nd = Infinity;
        for (const p of R.pages) {
          const L = p.lwa;
          const rel = (L.left + L.width / 2 - R.x - vw / 2) / vw;
          const d = Math.abs(rel);
          if (d < nd) { nd = d; nearest = L.k; }
          if (d > 1.4) continue;
          // v11: the pages sit still in the collage (no parallax / flutter)
          if (!L.placed) {
            L.placed = true;
            p.style.translate = `0 ${L.baseY}svh`;
            p.style.rotate = `${L.baseR}deg`;
          }
        }
        preload(R);
        if (R.active) {
          shown = R;
          hint.classList.toggle('is-gone', R.x > 40);
        }
      }
      document.body.classList.toggle('lwa-in-rail', !!shown);
      if (!shown) hint.classList.add('is-gone');

      // whole-book progress + the number of the page in the middle
      if (t - lastCount > 120) {
        lastCount = t;
        const bookTop = scrollTopOf(book), total = Math.max(1, book.offsetHeight - vh);
        const pr = clamp((st - bookTop) / total, 0, 1);
        progress.style.setProperty('--p', pr.toFixed(4));
        let mid = 0, md = Infinity;
        for (const p of pages) {
          const r = p.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) continue;
          const d = Math.hypot(r.left + r.width / 2 - vw / 2, r.top + r.height / 2 - vh / 2);
          if (d < md) { md = d; mid = p.lwa.i; }
        }
        if (md < Infinity) count.textContent = `${String(mid + 1).padStart(2, '0')} / ${pages.length}`;
        document.body.classList.toggle('lwa-show-progress', st > bookTop + vh * 0.5 && pr < 0.97);
      }

      // wet ink on the scan closest to the centre of the screen (desktop)
      if (t - lastWet > (touch ? 220 : 125) && (lifted || wetPage)) {
        lastWet = t;
        // drift back and forth (the noise only covers the page, so no endless drift)
        const wx2 = 140 * Math.sin(t / 3000);
        const wy2 = 40 * Math.sin(t / 4100);
        wetFlow.setAttribute('dx', wx2.toFixed(1));
        wetFlow.setAttribute('dy', wy2.toFixed(1));
        if (t - lastPick > 150) {
          lastPick = t;
          // v11: the ink only comes alive on the page you touch
          const best = lifted && !lifted.classList.contains('lw-cutout-page') ? lifted : null;
          if (best !== wetPage) {
            if (wetPage) wetPage.lwa.paper.firstElementChild.style.filter = '';
            wetPage = best;
            if (wetPage) wetPage.lwa.paper.firstElementChild.style.filter = 'url(#lwa-wet)';
          }
        }
      }

      if (lifted && t - lastBoil > 160) {
        lastBoil = t;
        turbulence.setAttribute('seed', String(1 + Math.floor(Math.random() * 40)));
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    /* --- lightbox ----------------------------------------------------- */
    const closeSrc = document.querySelector('.lc-menu-close img')?.src || ARROW;
    const box = document.createElement('div');
    box.className = 'lwa-box';
    box.hidden = true;
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'BOOK');
    box.innerHTML =
      `<div class="lwa-box-art"></div>` +
      `<button class="lwa-close" type="button" aria-label="Stäng"><img src="${closeSrc}" alt=""></button>` +
      `<button class="lwa-prev" type="button" aria-label="Föregående"><img src="${ARROW}" alt=""></button>` +
      `<button class="lwa-next" type="button" aria-label="Nästa"><img src="${ARROW}" alt=""></button>` +
      `<span class="lwa-box-count"></span>`;
    document.body.append(box);
    const art = box.querySelector('.lwa-box-art');
    const boxCount = box.querySelector('.lwa-box-count');
    let current = 0;

    function fill(i) {
      current = (i + pages.length) % pages.length;
      const page = pages[current];
      const pending = page.querySelector('image[data-source]');
      if (pending) { pending.setAttribute('href', pending.dataset.source); delete pending.dataset.source; }
      const clone = page.lwa.paper.firstElementChild.cloneNode(true);
      if (clone.tagName === 'IMG') { clone.loading = 'eager'; clone.removeAttribute('width'); clone.removeAttribute('height'); }
      art.replaceChildren(clone);
      art.style.setProperty('--r', (page.offsetWidth / page.offsetHeight).toFixed(4));
      boxCount.textContent = `${String(current + 1).padStart(2, '0')} / ${pages.length}`;
      return page.getBoundingClientRect();
    }
    function open(i) {
      const from = fill(i);
      box.hidden = false;
      box.isOpen = true;
      scroller.style.overflow = 'hidden';
      const to = art.getBoundingClientRect();
      art.animate(
        [
          { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})` },
          { transform: 'none' },
        ],
        { duration: 520, easing: 'cubic-bezier(.2,.9,.25,1)' },
      );
      requestAnimationFrame(() => box.classList.add('is-open'));
      box.querySelector('.lwa-close').focus({ preventScroll: true });
    }
    function close() {
      box.classList.remove('is-open');
      box.isOpen = false;
      scroller.style.overflow = '';
      // bring the book to the page that was open
      const page = pages[current];
      const R = page.lwa.rail;
      if (R) {
        const want = clamp(page.lwa.left + page.lwa.width / 2 - vw / 2, 0, R.travel);
        scroller.scrollTop = R.top + want * RAIL_SPEED;
        R.x = want;
      } else {
        scroller.scrollTop = scrollTopOf(page) - (vh - page.offsetHeight) / 2;
      }
      setTimeout(() => { box.hidden = true; art.replaceChildren(); }, 350);
    }
    function step(dir) {
      art.classList.add('is-swap');
      art.style.translate = `${-dir * 24}px 0`;
      setTimeout(() => {
        fill(current + dir);
        art.style.translate = `${dir * 24}px 0`;
        requestAnimationFrame(() => {
          art.classList.remove('is-swap');
          art.style.translate = '0 0';
        });
      }, 180);
    }

    collage.addEventListener('click', (e) => {
      const page = e.target.closest('.lw-collage-page');
      if (page && !box.isOpen) open(page.lwa.i);
    });
    box.querySelector('.lwa-close').addEventListener('click', close);
    box.querySelector('.lwa-prev').addEventListener('click', () => step(-1));
    box.querySelector('.lwa-next').addEventListener('click', () => step(1));
    box.addEventListener('click', (e) => { if (e.target === box) close(); });
    addEventListener('keydown', (e) => {
      if (!box.isOpen) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    });
    let sx0 = null;
    box.addEventListener('pointerdown', (e) => { sx0 = e.clientX; });
    box.addEventListener('pointerup', (e) => {
      if (sx0 === null) return;
      const dx = e.clientX - sx0;
      sx0 = null;
      if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
    });
  }

  /* ------------------------------------------------------------------ */
  /* CAT: more cat behaviour (only while it stands still, so it never    */
  /* fights the walking / stalking / pouncing in lugazi-world.js)        */
  /* ------------------------------------------------------------------ */
  function initCat() {
    const cat = document.querySelector('.lc-cat');
    if (!cat || !cat.querySelector('.lc-cat-facing') || cat.dataset.lwa) return;
    cat.dataset.lwa = '1';

    // speech bits: "prrr", "mjau", "z z z"
    const bubble = document.createElement('span');
    bubble.className = 'lwa-cat-say';
    bubble.setAttribute('aria-hidden', 'true');
    cat.append(bubble);
    let sayTimer = 0;
    function say(text, ms) {
      clearTimeout(sayTimer);
      if (!text) { bubble.classList.remove('is-on'); return; }
      bubble.textContent = text;
      bubble.classList.add('is-on');
      if (ms) sayTimer = setTimeout(() => bubble.classList.remove('is-on'), ms);
    }

    const MOODS = ['lwa-stretch', 'lwa-groom', 'lwa-scratch'];
    const is = (c) => cat.classList.contains(c);
    const busy = () =>
      is('is-walking') || is('is-pouncing') || is('is-stalking') || is('is-catching') ||
      is('is-logo-perched') || is('is-logo-hopping');
    let moodTimer = 0, hovered = false, idleSince = performance.now();

    function mood(name, ms) {
      if (busy() || reduced.matches) return;
      MOODS.forEach((m) => cat.classList.remove(m));
      void cat.offsetWidth; // restart the animation
      cat.classList.add(name);
      clearTimeout(moodTimer);
      moodTimer = setTimeout(() => cat.classList.remove(name), ms);
    }
    function stopMoods() {
      clearTimeout(moodTimer);
      MOODS.forEach((m) => cat.classList.remove(m));
    }

    // now and then, while standing still: wash a paw, scratch, stretch
    function idleLife() {
      if (!busy() && !hovered && !is('lwa-sleep')) {
        const r = Math.random();
        if (r < 0.4) mood('lwa-groom', 2800);
        else if (r < 0.65) mood('lwa-scratch', 1500);
        else if (r < 0.85) mood('lwa-stretch', 1900);
      }
      setTimeout(idleLife, 6000 + Math.random() * 8000);
    }

    // curl up and sleep after 20 s without input (only while standing still)
    function sleepCheck() {
      if (performance.now() - idleSince > 20000 && !busy() && !is('lwa-sleep')) {
        stopMoods();
        cat.classList.add('lwa-sleep');
        say('z z z');
      }
      setTimeout(sleepCheck, 1000);
    }
    function wake() {
      idleSince = performance.now();
      if (!is('lwa-sleep')) return;
      cat.classList.remove('lwa-sleep');
      say('');
      setTimeout(() => mood('lwa-stretch', 1900), 150); // big stretch after a nap
    }
    addEventListener('pointermove', wake, { passive: true });
    addEventListener('scroll', wake, { passive: true, capture: true });
    addEventListener('keydown', wake);

    // tail gets restless when the pointer comes close; startle on a click nearby
    addEventListener('pointermove', (e) => {
      const r = cat.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      cat.classList.toggle('lwa-near', d < 220 && !hovered);
    }, { passive: true });
    addEventListener('pointerdown', (e) => {
      if (cat.contains(e.target)) return;
      const r = cat.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      if (d < 170 && !reduced.matches) {
        stopMoods();
        cat.classList.remove('lwa-startle');
        void cat.offsetWidth;
        cat.classList.add('lwa-startle');
        setTimeout(() => cat.classList.remove('lwa-startle'), 900);
      }
    }, { passive: true });

    // "mjau" when you click the cat (its own pause/walk toggle still works)
    cat.addEventListener('click', () => say(Math.random() < 0.3 ? 'mjaaau' : 'mjau', 1300));

    // purr + knead while hovered
    let purrTimer = 0;
    cat.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      hovered = true;
      purrTimer = setTimeout(() => {
        stopMoods();
        cat.classList.add('lwa-purr');
        say('prrr');
      }, 450);
    });
    cat.addEventListener('pointerleave', () => {
      hovered = false;
      clearTimeout(purrTimer);
      cat.classList.remove('lwa-purr');
      if (!is('lwa-sleep')) say('');
    });

    // touch: hold a finger on the cat and it purrs (no pause/walk toggle then)
    let holdTimer = 0, held = false;
    cat.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      held = false;
      holdTimer = setTimeout(() => {
        held = true;
        stopMoods();
        cat.classList.add('lwa-purr');
        say('prrr');
        navigator.vibrate?.(30);
      }, 420);
    }, { passive: true });
    const letGo = () => {
      clearTimeout(holdTimer);
      if (!held) return;
      setTimeout(() => { cat.classList.remove('lwa-purr'); if (!is('lwa-sleep')) say(''); }, 900);
    };
    cat.addEventListener('pointerup', letGo, { passive: true });
    cat.addEventListener('pointercancel', letGo, { passive: true });
    cat.addEventListener('contextmenu', (e) => { if (held) e.preventDefault(); });
    addEventListener('click', (e) => {
      if (held && cat.contains(e.target)) { held = false; e.stopImmediatePropagation(); e.preventDefault(); }
    }, true);

    if (!reduced.matches) {
      setTimeout(idleLife, 5000);
      setTimeout(sleepCheck, 1000);
    }
  }

  /* ------------------------------------------------------------------ */
  /* TREE FACE (home, second artwork): the tree grows in, the blue lives,*/
  /* the strings float, the tree bends away from the pointer, seeds fall,*/
  /* and the layers move at different depths while you scroll            */
  /* ------------------------------------------------------------------ */
  function initTreeFace() {
    const svg = document.querySelector('svg.lw-plant');
    const scene = svg?.closest('.lw-scene');
    const base = svg?.querySelector('image[mask="url(#lw-tree-base)"]');
    const baseMask = svg?.querySelector('#lw-tree-base');
    const branch = svg?.querySelector('.lw-branches');
    const treeClip = branch?.parentNode;
    if (!svg || !scene || !base || !baseMask || !branch || svg.dataset.lwa || reduced.matches) return;
    svg.dataset.lwa = '1';
    const ns = 'http://www.w3.org/2000/svg';
    const href = base.getAttribute('href');
    const rich = true; // v9: also on phones (flow is updated less often there)
    const defs = svg.querySelector('defs');
    const node = (tag, attrs = {}, html = '') => {
      const el = document.createElementNS(ns, tag);
      for (const k in attrs) el.setAttribute(k, attrs[k]);
      if (html) el.innerHTML = html;
      return el;
    };
    // blue-ink mask used by the filters: alpha = 3*(B-R) - .15
    const BLUE = '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -3 0 3 0 -0.15';

    defs.insertAdjacentHTML('beforeend',
      // strings under the head: fade the original out, a floating copy in
      '<linearGradient id="lwa-down" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#fff"/><stop offset="1" stop-color="#000"/></linearGradient>' +
      '<linearGradient id="lwa-up" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#000"/><stop offset="1" stop-color="#fff"/></linearGradient>' +
      '<mask id="lwa-strings" maskUnits="userSpaceOnUse" x="0" y="0" width="1408" height="1132">' +
      '<rect y="560" width="1408" height="110" fill="url(#lwa-up)"/><rect y="670" width="1408" height="470" fill="#fff"/></mask>' +
      '<filter id="lwa-float" x="-5%" y="-5%" width="110%" height="110%" color-interpolation-filters="sRGB">' +
      `<feColorMatrix in="SourceGraphic" type="matrix" values="${BLUE}" result="b"/>` +
      '<feComposite in="SourceGraphic" in2="b" operator="out" result="ink"/>' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.004 0.011" numOctaves="2" seed="4" result="n"/>' +
      '<feOffset in="n" dx="0" dy="0" result="flow"/>' +
      '<feDisplacementMap in="ink" in2="flow" scale="15" xChannelSelector="R" yChannelSelector="G"/>' +
      '</filter>' +
      // blue on its own layer: ripples + glints of light
      '<filter id="lwa-blue" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">' +
      `<feColorMatrix in="SourceGraphic" type="matrix" values="${BLUE}" result="mask"/>` +
      '<feComposite in="SourceGraphic" in2="mask" operator="in" result="blue"/>' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.005 0.02" numOctaves="2" seed="9" result="n"/>' +
      '<feOffset in="n" dx="0" dy="0" result="flow"/>' +
      '<feDisplacementMap in="blue" in2="flow" scale="8" xChannelSelector="R" yChannelSelector="G" result="rip"/>' +
      '<feColorMatrix in="flow" type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 1 0 0 0 0" result="lum"/>' +
      '<feComponentTransfer in="lum" result="bands"><feFuncA type="table" tableValues="0 0 0 0 0 .32 0 0 0 0 0"/></feComponentTransfer>' +
      '<feComposite in="bands" in2="rip" operator="in" result="glint"/>' +
      '<feMerge><feMergeNode in="rip"/><feMergeNode in="glint"/></feMerge>' +
      '</filter>' +
      // everything except the blue (the blue is drawn on its own layer)
      '<filter id="lwa-noblue" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">' +
      `<feColorMatrix in="SourceGraphic" type="matrix" values="${BLUE}" result="b"/>` +
      '<feComposite in="SourceGraphic" in2="b" operator="out"/>' +
      '</filter>' +
      // the tree grows in from the trunk upward
      '<linearGradient id="lwa-grow-edge" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#000"/><stop offset=".14" stop-color="#fff"/><stop offset="1" stop-color="#fff"/></linearGradient>' +
      '<mask id="lwa-grow" maskUnits="userSpaceOnUse" x="0" y="0" width="1408" height="1132">' +
      '<rect class="lwa-grow-rect" x="400" y="0" width="520" height="800" fill="url(#lwa-grow-edge)"/></mask>',
    );
    // hide the original strings below the head with a soft edge
    // (they are redrawn on their own floating layer)
    baseMask.append(
      node('rect', { y: 560, width: 1408, height: 110, fill: 'url(#lwa-down)' }),
      node('rect', { y: 670, width: 1408, height: 470, fill: '#000' }),
    );

    // layer: blue (under the drawing)
    let blueLayer = null;
    if (rich) {
      blueLayer = node('g', { class: 'lwa-blue-layer' });
      blueLayer.append(node('image', { href, width: 1408, height: 1132, filter: 'url(#lwa-blue)', mask: 'url(#lw-tree-base)' }));
      base.before(blueLayer);
      base.setAttribute('filter', 'url(#lwa-noblue)');
    }
    // layer: floating strings (over the drawing)
    const strings = node('g', { class: 'lwa-strings', mask: 'url(#lwa-strings)' });
    strings.append(node('image', { href, width: 1408, height: 1132, filter: rich ? 'url(#lwa-float)' : '' }));
    base.after(strings);

    // tree: wind wrapper + grow mask
    const wind = node('g', { class: 'lwa-wind' });
    treeClip.insertBefore(wind, branch);
    wind.append(branch);
    treeClip.setAttribute('mask', 'url(#lwa-grow)');
    const growRect = defs.querySelector('.lwa-grow-rect');

    // falling seeds
    const seeds = node('g', { class: 'lwa-seeds', 'aria-hidden': 'true' });
    svg.append(seeds);

    const floatFlow = defs.querySelector('#lwa-float feOffset');
    const blueFlow = defs.querySelector('#lwa-blue feOffset');

    /* grow in each time the scene comes into view */
    let grown = false;
    function grow() {
      growRect.animate(
        [{ transform: 'translateY(560px)' }, { transform: 'translateY(0px)' }],
        { duration: 2600, easing: 'cubic-bezier(.25,.8,.3,1)', fill: 'both' },
      );
      for (let k = 0; k < 6; k++) setTimeout(seed, 1400 + k * 260);
    }
    growRect.style.transform = 'translateY(560px)';
    let visible = false;
    new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (en.intersectionRatio > 0.35 && !grown) { grown = true; grow(); }
      if (!en.isIntersecting && grown) {
        grown = false;
        growRect.getAnimations().forEach((a) => a.cancel());
        growRect.style.transform = 'translateY(560px)';
      }
    }, { threshold: [0, 0.35] }).observe(scene);

    /* wind: the tree leans away from the pointer */
    let pxSvg = null, pySvg = null, lean = 0, leanV = 0;
    let pointAt = 0;
    onPoint((x, y) => {
      const r = svg.getBoundingClientRect();
      const s = Math.min(r.width / 1408, r.height / 1132);
      const ox = r.left + (r.width - 1408 * s) / 2, oy = r.top + (r.height - 1132 * s) / 2;
      pxSvg = (x - ox) / s;
      pySvg = (y - oy) / s;
      pointAt = performance.now();
    });

    /* seeds drifting down from the canopy */
    const SHAPES = [
      () => `<circle r="${(5 + Math.random() * 5).toFixed(1)}"/>`,
      () => '<path d="M0 0c6-6 14-2 12 5s-12 7-14 0 6-13 16-9" fill="none" stroke="#000" stroke-width="4" stroke-linecap="round"/>',
      () => '<path d="M-6 0c4-9 10-9 12 0-4 7-8 7-12 0z"/>',
    ];
    function seed() {
      if (!visible || seeds.childElementCount > 14) return;
      const a = Math.random() * Math.PI * 2;
      const x0 = 649 + Math.cos(a) * 95 * Math.random();
      const y0 = 230 + Math.sin(a) * 70 * Math.random();
      const el = node('g', { fill: '#000' }, SHAPES[Math.floor(Math.random() * SHAPES.length)]());
      seeds.append(el);
      const drift = (Math.random() - 0.5) * 260, fall = 620 + Math.random() * 280;
      const spin = (Math.random() - 0.5) * 540, dur = 5200 + Math.random() * 3000;
      const sway = 40 + Math.random() * 50;
      el.animate(
        [
          { transform: `translate(${x0}px,${y0}px) rotate(0deg) scale(.2)`, opacity: 0 },
          { transform: `translate(${x0 + sway}px,${y0 + fall * 0.15}px) rotate(${spin * 0.2}deg) scale(1)`, opacity: 1, offset: 0.12 },
          { transform: `translate(${x0 - sway + drift * 0.4}px,${y0 + fall * 0.45}px) rotate(${spin * 0.5}deg) scale(1)`, opacity: 1, offset: 0.45 },
          { transform: `translate(${x0 + sway + drift * 0.8}px,${y0 + fall * 0.8}px) rotate(${spin * 0.8}deg) scale(.9)`, opacity: 0.8, offset: 0.8 },
          { transform: `translate(${x0 + drift}px,${y0 + fall}px) rotate(${spin}deg) scale(.8)`, opacity: 0 },
        ],
        { duration: dur, easing: 'linear' },
      ).onfinish = () => el.remove();
    }
    (function seedLoop() {
      seed();
      setTimeout(seedLoop, 1300 + Math.random() * 1500);
    })();

    /* one loop for flow, wind and depth */
    let lastFlow = 0;
    function tick(t) {
      if (visible) {
        // ~10 fps flow for the filters (hand-animated feel, cheaper)
        if (rich && t - lastFlow > (touch ? 160 : 100)) {
          lastFlow = t;
          floatFlow.setAttribute('dx', (60 * Math.sin(t / 2600)).toFixed(1));
          floatFlow.setAttribute('dy', (160 * Math.sin(t / 3100)).toFixed(1));
          blueFlow.setAttribute('dx', (120 * Math.sin(t / 3400)).toFixed(1));
          blueFlow.setAttribute('dy', (30 * Math.sin(t / 2300)).toFixed(1));
        }
        // wind: spring toward a lean away from the pointer
        let want = 0;
        if (touch && performance.now() - pointAt > 1500) pxSvg = null; // finger lifted
        if (pxSvg !== null) {
          const dx = pxSvg - 649, dy = pySvg - 300;
          const d = Math.hypot(dx, dy);
          if (d < 520) want = clamp(dx / 260, -1, 1) * (1 - d / 520) * 14;
        }
        want += Math.sin(t / 1700) * 1.2; // a light breeze
        leanV += (want - lean) * 0.06;
        leanV *= 0.86;
        lean += leanV;
        wind.setAttribute('transform', `translate(649 480) skewX(${lean.toFixed(2)}) translate(-649 -480)`);
        // depth: layers drift at different speeds while scrolling
        const r = scene.getBoundingClientRect();
        const p = clamp(-r.top / Math.max(1, r.height - innerHeight), -0.5, 1.5) - 0.5;
        if (blueLayer) blueLayer.setAttribute('transform', `translate(${(p * 18).toFixed(1)} ${(p * 26).toFixed(1)})`);
        strings.setAttribute('transform', `translate(0 ${(p * -22).toFixed(1)})`);
        seeds.setAttribute('transform', `translate(0 ${(p * -40).toFixed(1)})`);
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ */
  /* FIGURE (home, third artwork): crosses stitch in, the body breathes, */
  /* the hair smokes, fingers twitch, the ear twists, it turns toward    */
  /* you, leaves an echo on fast scroll, and the crosses fly on click    */
  /* ------------------------------------------------------------------ */
  function initFigure() {
    const svg = document.querySelector('#lugazi-scene-3 svg.lw-image');
    const scene = svg?.closest('.lw-scene');
    const baseMask = svg?.querySelector('#lw-cross-base');
    const base = svg?.querySelector('image[mask="url(#lw-cross-base)"]');
    if (!svg || !scene || !baseMask || !base || svg.dataset.lwa || reduced.matches) return;
    svg.dataset.lwa = '1';
    const ns = 'http://www.w3.org/2000/svg';
    const href = base.getAttribute('href');
    const rich = true; // v9: also on phones
    const defs = svg.querySelector('defs');
    const node = (tag, attrs = {}, html = '') => {
      const el = document.createElementNS(ns, tag);
      for (const k in attrs) if (attrs[k] !== '') el.setAttribute(k, attrs[k]);
      if (html) el.innerHTML = html;
      return el;
    };
    const W = 1044, H = 1507;
    const img = (attrs = {}) => node('image', { href, width: W, height: H, ...attrs });

    // Regions that get their own moving layer. The original is faded out
    // of the base drawing (soft edge) and a moving copy is faded in.
    //   kind 'rect': x, y, w, h, f = soft edge size
    //   kind 'circle': cx, cy, r
    const REGIONS = [
      { id: 'smoke-r', rect: [660, 0, 384, 760], f: 70, rich: true },
      { id: 'smoke-l', rect: [50, 50, 300, 300], f: 50, rich: true },
      { id: 'hand-l', rect: [10, 1150, 260, 260], f: 40, origin: [235, 1215] },
      { id: 'hand-r', rect: [510, 1320, 300, 180], f: 40, origin: [640, 1390] },
      { id: 'ear', circle: [135, 585, 105] },
    ].filter((r) => !r.rich || rich);

    let html =
      '<radialGradient id="lwa-fig-in"><stop offset=".78" stop-color="#fff"/><stop offset="1" stop-color="#000"/></radialGradient>' +
      '<radialGradient id="lwa-fig-out"><stop offset=".78" stop-color="#000"/><stop offset="1" stop-color="#fff"/></radialGradient>' +
      '<filter id="lwa-smoke" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.012 0.006" numOctaves="2" seed="3" result="n"/>' +
      '<feOffset in="n" dx="0" dy="0" result="flow"/>' +
      '<feDisplacementMap in="SourceGraphic" in2="flow" scale="6" xChannelSelector="R" yChannelSelector="G"/>' +
      '</filter>';
    const featherRect = (id, [x, y, w, h], f, show) => {
      // a white (show) or black (hide) rect with blurred edges
      const fid = `lwa-fb-${id}`;
      html += `<filter id="${fid}" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${f / 2.5}"/></filter>`;
      return `<rect x="${x + f / 2}" y="${y + f / 2}" width="${w - f}" height="${h - f}" fill="${show ? '#fff' : '#000'}" filter="url(#${fid})"/>`;
    };
    const hides = [];
    REGIONS.forEach((R) => {
      let show, hide;
      if (R.rect) {
        show = featherRect(R.id + 's', R.rect, R.f, true);
        hide = featherRect(R.id + 'h', R.rect, R.f, false);
      } else {
        const [cx, cy, r] = R.circle;
        show = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#lwa-fig-in)"/>`;
        hide = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#lwa-fig-out)"/>`;
      }
      html += `<mask id="lwa-m-${R.id}" maskUnits="userSpaceOnUse" x="0" y="0" width="${W}" height="${H}">${show}</mask>`;
      hides.push(hide);
    });
    defs.insertAdjacentHTML('beforeend', html);
    // hide the moving regions in the original drawing
    baseMask.insertAdjacentHTML('beforeend', hides.join(''));

    // everything that breathes together goes in one group
    const body = node('g', { class: 'lwa-fig-body' });
    const kids = [...svg.childNodes].filter((n) => n !== defs);
    svg.append(body);
    kids.forEach((k) => body.append(k));

    // moving layers, right after the base drawing (crosses stay on top)
    const layers = {};
    let after = base;
    REGIONS.forEach((R) => {
      const g = node('g', { class: `lwa-fig-part lwa-fig-${R.id}`, mask: `url(#lwa-m-${R.id})` });
      const inner = node('g', { class: 'lwa-fig-move' });
      inner.append(img(R.id.startsWith('smoke') ? { filter: 'url(#lwa-smoke)' } : {}));
      g.append(inner);
      if (R.origin) inner.style.transformOrigin = `${R.origin[0]}px ${R.origin[1]}px`;
      if (R.circle) inner.style.transformOrigin = `${R.circle[0]}px ${R.circle[1]}px`;
      after.after(g);
      after = g;
      layers[R.id] = inner;
    });

    // crosses: give each its own inner group we can animate freely
    const crosses = [...body.querySelectorAll('g[data-cross]')].map((g) => {
      const inner = node('g', { class: 'lwa-x' });
      while (g.firstChild) inner.append(g.firstChild);
      g.append(inner);
      return inner;
    });

    /* stitch the crosses in, one by one, each time the figure appears */
    let shown = false, visible = false; // v9: stitch only the first time
    function stitch() {
      crosses.forEach((x, i) => x.animate(
        [{ scale: '0', rotate: '-90deg' }, { scale: '1.35', rotate: '10deg', offset: 0.7 }, { scale: '1', rotate: '0deg' }],
        { duration: 420, delay: 250 + i * 32, easing: 'ease-out', fill: 'backwards' },
      ));
    }
    new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (en.intersectionRatio > 0.4 && !shown) { shown = true; stitch(); }
    }, { threshold: [0, 0.4] }).observe(scene);

    /* hover: a spin wave runs down the spine */
    let waveAt = 0;
    svg.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || performance.now() - waveAt < 2500) return;
      waveAt = performance.now();
      crosses.forEach((x, i) => x.animate(
        [{ rotate: '0deg' }, { rotate: '180deg', scale: '1.25' }, { rotate: '360deg', scale: '1' }],
        { duration: 520, delay: i * 45, easing: 'ease-in-out' },
      ));
    });

    /* click: the crosses fly off and get pulled back in */
    svg.style.cursor = 'pointer';
    svg.addEventListener('click', () => {
      crosses.forEach((x, i) => {
        const a = Math.random() * Math.PI * 2, d = 160 + Math.random() * 260;
        const tx = Math.cos(a) * d, ty = Math.sin(a) * d, r = (Math.random() - 0.5) * 720;
        x.animate(
          [
            { translate: '0 0', rotate: '0deg' },
            { translate: `${tx}px ${ty}px`, rotate: `${r}deg`, offset: 0.35 },
            { translate: `${tx * 0.9}px ${ty * 0.9}px`, rotate: `${r * 1.1}deg`, offset: 0.55 },
            { translate: '0 0', rotate: '0deg' },
          ],
          { duration: 1500, delay: i * 12, easing: 'cubic-bezier(.3,.7,.2,1)' },
        );
      });
    });

    /* fingers twitch now and then */
    function twitch() {
      if (visible) {
        const hand = Math.random() < 0.5 ? layers['hand-l'] : layers['hand-r'];
        const s = hand === layers['hand-l'] ? -1 : 1;
        hand?.animate(
          [{ rotate: '0deg' }, { rotate: `${s * 4}deg` }, { rotate: `${s * -1.5}deg` }, { rotate: `${s * 2.5}deg` }, { rotate: '0deg' }],
          { duration: 700, easing: 'ease-in-out' },
        );
      }
      setTimeout(twitch, 2200 + Math.random() * 3500);
    }
    setTimeout(twitch, 2000);

    /* pointer for the 3D turn */
    let tx = 0, ty = 0, rx = 0, ry = 0, turnAt = 0;
    onPoint((x, y) => {
      const r = svg.getBoundingClientRect();
      tx = clamp((x - (r.left + r.width / 2)) / (r.width * 0.9), -1, 1);
      ty = clamp((y - (r.top + r.height / 2)) / (r.height * 0.9), -1, 1);
      turnAt = performance.now();
    });

    /* one loop: smoke flow, ear twist, 3D turn */
    let lastFlow = 0;
    const smokeFlow = defs.querySelector('#lwa-smoke feOffset');
    function tick(t) {
      if (visible) {
        if (rich && t - lastFlow > (touch ? 160 : 110)) {
          lastFlow = t;
          // smoke drifts slowly upward and sideways (oscillating, never runs off)
          smokeFlow.setAttribute('dx', (36 * Math.sin(t / 2800)).toFixed(1));
          smokeFlow.setAttribute('dy', (90 * Math.sin(t / 3600)).toFixed(1));
        }
        if (touch && performance.now() - turnAt > 1500) { tx *= 0.9; ty *= 0.9; } // ease back
        if (layers.ear) layers.ear.style.rotate = `${(Math.sin(t / 1400) * 6).toFixed(2)}deg`;
        if (rich) {
          rx += (tx - rx) * 0.06;
          ry += (ty - ry) * 0.06;
          svg.style.transform = `perspective(1400px) rotateY(${(rx * 9).toFixed(2)}deg) rotateX(${(-ry * 6).toFixed(2)}deg)`;
        }
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ */
  /* FACE (home, first artwork): the moving eyes also blink              */
  /* ------------------------------------------------------------------ */
  function initFaceBlink() {
    const face = document.querySelector('.lw-face-live');
    if (!face || face.dataset.lwa || reduced.matches) return;
    face.dataset.lwa = '1';
    const ns = 'http://www.w3.org/2000/svg';
    const defs = face.querySelector('defs');
    const lids = [];
    face.querySelectorAll('[data-face-eye]').forEach((g, i) => {
      const cx = [333, 893][i], cy = 218;
      const clip = document.createElementNS(ns, 'clipPath');
      clip.id = `lwa-lid-clip-${i}`;
      clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
      clip.innerHTML = `<ellipse cx="${cx}" cy="${cy}" rx="47" ry="48"/>`;
      defs.append(clip);
      const holder = document.createElementNS(ns, 'g');
      holder.setAttribute('clip-path', `url(#lwa-lid-clip-${i})`);
      // the lid: paper above a curved ink line; it slides down to close
      const lid = document.createElementNS(ns, 'g');
      lid.innerHTML =
        `<rect x="${cx - 60}" y="${cy - 160}" width="120" height="163" fill="#fff"/>` +
        `<path d="M ${cx - 50} ${cy - 4} Q ${cx} ${cy + 22} ${cx + 50} ${cy - 4}" fill="none" stroke="#000" stroke-width="13" stroke-linecap="round"/>`;
      lid.style.transform = 'translateY(-110px)';
      holder.append(lid);
      g.append(holder);
      lids.push(lid);
    });
    function blink() {
      lids.forEach((lid) => lid.animate(
        [{ transform: 'translateY(-110px)' }, { transform: 'translateY(0px)', offset: 0.45 }, { transform: 'translateY(0px)', offset: 0.6 }, { transform: 'translateY(-110px)' }],
        { duration: 340, easing: 'ease-in-out' },
      ));
      setTimeout(blink, Math.random() < 0.2 ? 420 : 3000 + Math.random() * 4000);
    }
    setTimeout(blink, 1500);
  }

  /* ------------------------------------------------------------------ */
  /* HOVER: jelly + magnetic nav icons, ink splat on click               */
  /* ------------------------------------------------------------------ */
  function initHover() {
    if (reduced.matches) return;
    if (fine.matches) {
      const icons = [...document.querySelectorAll('.lc-nav .lc-menu-toggle img, .lc-nav .lc-brand, .lc-nav .lc-bag-link img')];
      icons.forEach((img) => {
        const target = img.closest('a, button') || img;
        target.addEventListener('pointerenter', () => {
          img.animate(
            [{ scale: '1 1' }, { scale: '1.14 .88' }, { scale: '.93 1.08' }, { scale: '1.04 .97' }, { scale: '1 1' }],
            { duration: 620, easing: 'ease-out', composite: 'add' },
          );
        });
      });
      // (touch handled below)
      // icons lean toward the pointer when it comes close
      addEventListener('pointermove', (e) => {
        for (const img of icons) {
          const r = img.getBoundingClientRect();
          const dx = e.clientX - (r.left + r.width / 2);
          const dy = e.clientY - (r.top + r.height / 2);
          const d = Math.hypot(dx, dy);
          const pull = d < 120 ? (1 - d / 120) * 0.16 : 0;
          img.style.translate = pull ? `${(dx * pull).toFixed(1)}px ${(dy * pull).toFixed(1)}px` : '';
        }
      }, { passive: true });
    }
    if (!fine.matches) {
      // touch: the icons wobble when tapped
      document.querySelectorAll('.lc-nav .lc-menu-toggle img, .lc-nav .lc-brand, .lc-nav .lc-bag-link img').forEach((img) => {
        (img.closest('a, button') || img).addEventListener('pointerdown', () => {
          img.animate(
            [{ scale: '1 1' }, { scale: '1.16 .86' }, { scale: '.92 1.09' }, { scale: '1.04 .97' }, { scale: '1 1' }],
            { duration: 560, easing: 'ease-out', composite: 'add' },
          );
        }, { passive: true });
      });
    }
    // ink splat where you click / tap
    const ns = 'http://www.w3.org/2000/svg';
    addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.target.closest('input, textarea, select, .lwa-box')) return;
      const s = document.createElementNS(ns, 'svg');
      s.setAttribute('class', 'lwa-splat');
      s.setAttribute('viewBox', '-50 -50 100 100');
      s.setAttribute('aria-hidden', 'true');
      // a wobbly blob + a few droplets
      const n = 11, pts = [];
      for (let k = 0; k < n; k++) {
        const a = (k / n) * Math.PI * 2, rr = 13 + Math.random() * 9;
        pts.push([Math.cos(a) * rr, Math.sin(a) * rr]);
      }
      let d = `M ${pts[0][0]} ${pts[0][1]}`;
      for (let k = 0; k < n; k++) {
        const p0 = pts[k], p1 = pts[(k + 1) % n];
        d += ` Q ${p0[0] * 1.25} ${p0[1] * 1.25} ${(p0[0] + p1[0]) / 2} ${(p0[1] + p1[1]) / 2}`;
      }
      let drops = '';
      for (let k = 0; k < 5; k++) {
        const a = Math.random() * Math.PI * 2, rr = 26 + Math.random() * 18;
        drops += `<circle cx="${(Math.cos(a) * rr).toFixed(1)}" cy="${(Math.sin(a) * rr).toFixed(1)}" r="${(1.5 + Math.random() * 3.5).toFixed(1)}"/>`;
      }
      s.innerHTML = `<path d="${d} Z"/>${drops}`;
      s.style.left = `${e.clientX}px`;
      s.style.top = `${e.clientY}px`;
      s.style.rotate = `${Math.random() * 360}deg`;
      document.body.append(s);
      s.addEventListener('animationend', () => s.remove());
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* EYES ON PHONES: the face's eyes follow your finger anywhere on the  */
  /* screen, look the way you scroll, and wander a little on their own.  */
  /* (On desktop lugazi-world.js already follows the mouse.)             */
  /* ------------------------------------------------------------------ */
  function initTouchEyes() {
    if (fine.matches || reduced.matches) return;
    const svg = document.querySelector('.lw-face-live');
    const groups = svg ? [...svg.querySelectorAll('[data-face-eye]')] : [];
    if (!svg || !groups.length || svg.dataset.lwaEyes) return;
    svg.dataset.lwaEyes = '1';
    const ns = 'http://www.w3.org/2000/svg';
    const eyes = groups.map((g, i) => {
      const inner = document.createElementNS(ns, 'g');
      while (g.firstChild) inner.append(g.firstChild);
      g.append(inner);
      return { g, inner, cx: [333, 893][i], cy: 218 };
    });
    let fx = 0, fy = 0, fingerAt = -1e9;
    onPoint((x, y) => { fx = x; fy = y; fingerAt = performance.now(); });
    const wrapper = document.querySelector('.page-wrapper');
    const scroller = () =>
      wrapper && /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY) ? wrapper : document.scrollingElement;
    let sc = scroller(), lastTop = sc.scrollTop, vel = 0;
    addEventListener('resize', () => { sc = scroller(); });
    let ex = 0, ey = 0, amount = 0;
    function tick(t) {
      const top = sc.scrollTop;
      vel += (top - lastTop - vel) * 0.15;
      lastTop = top;
      const r = svg.getBoundingClientRect();
      if (r.bottom > 0 && r.top < innerHeight) {
        const s = Math.min(r.width / 1180, r.height / 1333);
        const ox = r.left + (r.width - 1180 * s) / 2, oy = r.top + (r.height - 1333 * s) / 2;
        let wantX, wantY, want = 1;
        const now = performance.now();
        if (now - fingerAt < 1800) {
          // look at the finger (in drawing coordinates)
          wantX = (fx - ox) / s;
          wantY = (fy - oy) / s;
        } else {
          // look the way the page moves, and wander a little
          const v = clamp(vel / 12, -1, 1);
          wantX = 613 + Math.sin(t / 2300) * 380 + Math.sin(t / 900) * 60;
          wantY = 218 + v * 520 + Math.sin(t / 3100) * 160;
        }
        // lugazi-world.js moves the eyes itself while the face is touched
        if (eyes[0].g.hasAttribute('transform')) want = 0;
        amount += (want - amount) * 0.12;
        let first = true;
        for (const e of eyes) {
          const dx = wantX - e.cx, dy = wantY - e.cy, d = Math.max(100, Math.hypot(dx, dy));
          const tx = (dx / d) * 18, ty = (dy / d) * 12;
          if (first) { ex += (tx - ex) * 0.16; ey += (ty - ey) * 0.16; first = false; }
          e.inner.setAttribute('transform', `translate(${(ex * amount).toFixed(2)} ${(ey * amount).toFixed(2)})`);
        }
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ */
  /* TRAIL: motifs from the drawings ($, stars, crosses, sparks...) drop */
  /* out behind the mouse / finger, spin, sink and fade like ink.        */
  /* Replaces the old eye trail of lugazi-world.js (hidden in CSS).      */
  /* ------------------------------------------------------------------ */
  function initTrail() {
    if (reduced.matches || document.querySelector('.lwa-trail')) return;
    // motifs traced from the drawings (money bag, bottle, spirals, figure),
    // each fitted in a 100 x 100 box around 0,0
    const SHAPES = [
      'M1 45L-0 44 1 42C2 39 2 28 1 29C0 29 -2 29 -4 30L-8 30 -8 37C-8 45 -8 45 -10 46C-12 46 -16 45 -17 43C-18 42 -17 38 -16 32C-16 31 -16 31 -20 30C-22 30 -25 30 -26 29C-29 28 -30 27 -32 22C-32 20 -32 20 -25 21C-23 22 -19 22 -18 22L-15 22 -15 16C-14 12 -14 8 -14 7C-14 5 -14 2 -14 1L-14 -2 -20 -2C-25 -2 -26 -2 -28 -3C-31 -5 -34 -9 -34 -12C-34 -14 -32 -21 -31 -24C-29 -28 -23 -32 -17 -34C-15 -34 -15 -34 -15 -35C-15 -39 -14 -45 -13 -45C-12 -46 -10 -47 -9 -46C-8 -46 -8 -44 -8 -40L-8 -35 -7 -36C-6 -36 -4 -36 -2 -36L2 -37 2 -40L2 -44 5 -44C6 -44 8 -45 9 -46L11 -47 13 -46L15 -44 15 -41L14 -37 19 -37C23 -36 23 -36 25 -35L26 -33 23 -33C21 -33 18 -34 17 -34L14 -34 14 -32C14 -27 13 -12 12 -10C12 -10 13 -9 16 -9C29 -6 35 -3 35 3C35 6 34 7 26 15C19 21 18 22 14 24C11 25 8 26 8 26C7 26 8 39 9 41C9 43 9 45 7 46C4 47 3 47 1 45ZM-2 22L1 22 1 19C1 18 1 16 1 15C0 13 0 12 1 10C1 8 2 5 2 1L2 -5 -1 -4C-3 -4 -5 -4 -6 -4L-8 -3 -8 10L-8 23 -7 23C-7 23 -4 23 -2 22ZM12 19C17 15 20 13 23 9C27 4 27 3 24 2C23 1 20 0 17 -1C15 -2 12 -3 12 -3C11 -3 9 7 8 14C8 16 8 19 8 20C7 21 8 21 12 19ZM-14 -12C-14 -15 -15 -20 -15 -23L-16 -28 -19 -24C-28 -16 -27 -10 -19 -8C-14 -6 -15 -6 -14 -12ZM-2 -9L2 -9 2 -21C2 -28 2 -34 2 -34C2 -34 -4 -33 -6 -32L-8 -32 -8 -20L-8 -7 -7 -8C-7 -8 -4 -8 -2 -9Z',
      'M20 34C19 33 16 32 15 30C13 29 11 27 9 25C7 24 4 22 2 21C-0 19 -2 18 -3 18C-4 18 -9 21 -13 23C-14 24 -16 25 -16 25C-16 25 -18 26 -20 28C-28 33 -32 36 -34 34C-36 33 -35 32 -32 29C-30 27 -28 25 -27 23C-26 22 -24 19 -23 18C-21 16 -20 14 -20 14C-19 13 -15 7 -16 7C-16 6 -23 -1 -24 -1C-24 -1 -26 -2 -28 -4C-30 -6 -32 -8 -33 -9C-34 -9 -35 -10 -36 -11C-37 -12 -39 -14 -41 -15C-43 -17 -45 -19 -45 -19C-45 -20 -46 -21 -46 -22C-46 -22 -45 -25 -44 -26C-43 -27 -43 -27 -42 -26C-42 -25 -41 -25 -40 -25C-40 -25 -39 -25 -39 -25C-39 -24 -33 -21 -32 -21C-31 -21 -30 -21 -30 -20C-29 -20 -28 -20 -27 -20C-26 -20 -25 -19 -24 -19C-24 -19 -22 -18 -21 -18C-20 -18 -18 -17 -16 -17C-15 -16 -12 -16 -10 -16C-9 -15 -7 -15 -7 -15C-6 -14 -3 -17 -3 -18C-3 -18 -2 -20 -1 -23C0 -25 2 -28 2 -29C2 -31 2 -32 4 -34C6 -36 8 -37 9 -35C9 -35 9 -34 10 -35C11 -35 12 -31 12 -29C12 -29 13 -27 13 -26C14 -25 14 -24 14 -23C14 -23 14 -21 15 -20C15 -19 16 -18 16 -17C17 -12 21 -11 34 -11C44 -11 44 -11 46 -8C47 -7 47 -7 45 -5C44 -4 43 -4 42 -4C42 -3 40 -3 40 -3C39 -2 38 -2 37 -2C37 -2 36 -2 36 -2C36 -1 35 -1 35 -1C33 -1 21 5 21 6C21 8 21 11 22 12C22 12 22 14 23 15C23 18 24 20 25 24C26 25 26 27 26 27C26 28 27 28 27 28C29 29 26 35 22 36C22 36 21 35 20 34ZM13 15C13 15 13 13 13 12L12 10 10 12L8 13 10 15C12 17 14 17 13 15ZM2 7C3 6 5 5 6 5C10 2 10 2 10 -1C10 -3 10 -4 9 -4C8 -6 2 -6 1 -6C-1 -5 -4 2 -4 4C-4 7 0 9 2 7ZM-11 -3C-10 -3 -10 -4 -9 -5C-9 -7 -9 -7 -17 -9C-19 -9 -19 -8 -15 -5C-12 -1 -12 -1 -11 -3Z',
      'M22 35C17 30 13 26 12 26C12 25 10 24 8 23C4 18 4 18 -1 24C-4 27 -6 29 -7 29C-7 30 -7 30 -7 31C-7 32 -14 39 -16 40C-18 41 -23 36 -21 35C-21 35 -21 34 -21 33C-21 33 -20 32 -20 32C-19 31 -19 31 -19 30C-19 30 -18 28 -17 26C-16 22 -15 20 -14 15C-14 14 -13 13 -13 12C-13 12 -12 10 -12 8L-12 4 -14 3C-15 2 -18 -0 -20 -2C-22 -4 -25 -6 -25 -7C-26 -7 -27 -8 -28 -8C-29 -9 -31 -10 -33 -11C-34 -12 -35 -13 -36 -13C-37 -14 -39 -15 -40 -16C-40 -16 -41 -17 -42 -17C-42 -18 -43 -18 -44 -18C-44 -19 -45 -20 -46 -21C-47 -23 -46 -25 -43 -26C-40 -28 -40 -28 -36 -26C-36 -25 -34 -25 -33 -25C-32 -24 -29 -23 -26 -22C-25 -22 -23 -21 -20 -21C-17 -21 -14 -20 -13 -20C-8 -19 -7 -21 -8 -31L-8 -38 -6 -40C-4 -43 -4 -43 -2 -43C1 -43 4 -41 4 -39C4 -39 4 -38 5 -38C5 -37 6 -37 6 -36C5 -36 7 -33 8 -31C9 -28 10 -25 10 -25C10 -25 11 -23 12 -22L14 -20 18 -20C27 -21 42 -22 43 -22C43 -22 44 -22 45 -22C49 -20 47 -16 41 -13C39 -12 37 -11 36 -10C36 -10 35 -9 34 -9C33 -8 31 -7 30 -6C29 -5 27 -3 26 -2C23 -0 22 2 25 6C25 7 25 8 25 8C25 9 26 10 26 10C27 11 27 12 27 12C27 13 27 14 28 15C29 18 30 20 30 22C30 24 31 25 31 25C32 25 32 26 32 27C32 27 32 28 33 29C33 29 33 30 33 30C33 31 34 32 34 33C35 34 35 35 36 37C36 41 35 43 32 43C30 43 29 42 22 35ZM18 15C18 13 15 9 14 9C14 9 12 10 12 11C12 11 13 12 14 13C15 15 18 16 18 15ZM7 2C10 -2 10 -2 9 -6C7 -9 6 -10 3 -11C1 -11 -0 -9 -1 -3C-1 -0 -1 -0 1 2C4 5 4 5 7 2Z',
      'M-4 46C-4 45 -5 44 -6 44C-9 42 -11 36 -10 32C-10 31 -9 28 -9 25C-9 22 -8 19 -8 17C-7 15 -7 15 -8 13C-10 10 -10 9 -24 9L-36 9 -39 7C-41 5 -42 4 -42 2C-42 1 -43 -1 -43 -1C-44 -3 -44 -5 -41 -9C-38 -12 -37 -12 -24 -12C-7 -12 -7 -12 -7 -28L-8 -39 -5 -41C-4 -43 -2 -44 -1 -45C-1 -45 0 -46 0 -46C0 -47 1 -47 4 -47C7 -47 7 -47 9 -45C14 -41 14 -41 14 -32C14 -28 14 -25 15 -25C15 -24 15 -23 14 -22C14 -20 14 -20 15 -18C16 -17 16 -16 16 -15C16 -15 17 -14 17 -13C18 -12 19 -12 24 -11C28 -11 31 -11 32 -11C36 -10 41 -7 43 -4C43 -2 43 -2 42 1C41 5 38 9 36 9C35 9 34 9 32 10C30 11 29 11 24 11C14 10 14 10 14 20C14 23 13 29 13 32C13 40 13 39 9 43L6 47 1 47C-3 47 -3 47 -4 46Z',
      'M-1 46C-4 45 -6 39 -4 36C-4 34 -1 30 0 30C2 30 2 29 1 27C1 26 0 23 0 21C-0 19 -1 17 -1 16C-1 16 -2 13 -2 11C-2 9 -3 6 -3 5L-4 3 -6 3C-7 3 -9 4 -10 4C-11 4 -14 5 -16 5C-21 5 -23 4 -26 2C-27 1 -27 1 -29 1C-33 3 -39 -0 -39 -4C-39 -8 -36 -11 -32 -11C-30 -11 -26 -9 -25 -7L-24 -6 -22 -7C-21 -8 -18 -11 -15 -15C-13 -17 -10 -18 -7 -18C-4 -19 -4 -19 -3 -20C-2 -29 -1 -32 2 -35C4 -37 5 -38 4 -39C4 -42 7 -47 10 -47C14 -47 16 -45 16 -41C15 -36 15 -34 16 -32C17 -30 18 -24 18 -20C18 -17 19 -16 21 -16C22 -16 23 -16 24 -17C28 -21 34 -21 37 -17C40 -13 38 -9 32 -8C31 -8 30 -8 30 -8C30 -8 30 -8 30 -8C31 -8 32 -4 33 -2C33 1 29 4 24 5C23 5 22 5 21 5C21 5 20 5 19 5C19 5 18 6 18 8C16 13 10 25 7 28L5 30 6 31C8 34 9 36 8 39C7 45 3 48 -1 46ZM8 11C9 8 10 5 11 2C12 -2 13 -3 19 -3C24 -3 25 -4 23 -5C21 -7 19 -8 16 -8C15 -8 14 -9 13 -10C11 -12 11 -12 11 -16C11 -20 9 -28 8 -29C8 -29 7 -29 7 -28C5 -26 5 -24 4 -17C4 -12 4 -11 1 -10C-0 -10 -1 -10 -2 -10C-2 -10 -4 -10 -5 -10C-8 -10 -8 -10 -11 -7C-13 -6 -14 -4 -14 -4C-14 -3 -11 -3 -7 -5C1 -7 4 -4 6 7C7 13 7 14 8 11Z',
      'M-25 19C-32 17 -36 13 -44 1C-46 -2 -47 -4 -47 -4C-46 -6 -45 -6 -43 -4L-41 -3 -41 -5C-42 -8 -41 -10 -39 -11C-33 -14 -26 -2 -29 5C-30 7 -32 8 -33 7C-36 6 -30 12 -27 14C-24 15 -19 15 -15 14C-11 12 -8 8 -3 -0C5 -14 15 -20 27 -20C36 -19 42 -13 45 -0C48 11 46 21 42 19C41 18 41 17 42 15C43 12 42 3 40 -4C36 -14 28 -17 18 -14C11 -11 6 -7 1 1C-4 11 -8 15 -14 18C-17 20 -22 20 -25 19Z',
      'M24 23C24 22 23 22 22 22C21 22 19 21 17 19C17 19 16 18 16 18C16 18 13 16 11 14C8 12 6 10 6 10C5 10 2 5 2 3C1 1 0 1 -1 3C-2 5 -3 6 -3 7C-3 7 -5 9 -6 10C-7 11 -10 14 -13 16C-18 21 -20 22 -25 21C-27 21 -29 21 -30 20C-32 19 -37 15 -38 13C-38 12 -39 11 -40 10C-42 7 -45 0 -46 -3C-46 -8 -46 -9 -45 -12C-44 -13 -44 -14 -44 -14C-44 -15 -44 -15 -44 -15C-43 -15 -43 -16 -42 -17C-41 -19 -37 -22 -35 -22C-35 -22 -34 -22 -34 -23C-33 -24 -22 -24 -21 -23C-21 -22 -20 -22 -19 -22C-17 -22 -11 -17 -7 -12C-6 -11 -4 -9 -3 -8C-1 -6 -1 -4 -0 -3C0 -0 1 -0 2 -3C3 -5 5 -7 8 -9C9 -10 11 -11 12 -12C13 -13 15 -15 16 -16C17 -16 19 -18 20 -19C22 -21 24 -22 24 -22C25 -22 26 -23 26 -23C26 -24 33 -24 34 -23C34 -23 35 -22 36 -22C37 -21 38 -20 39 -20C41 -18 46 -11 46 -10C46 -9 46 -8 46 -8C47 -8 47 -6 47 -2C47 3 47 5 46 5C46 5 46 6 46 6C46 8 42 14 39 17C36 20 35 20 31 22C27 24 24 24 24 23Z',
    ].map((d) => new Path2D(d));
    // [shape, weight]: dollar, star, star, cross, spark, squiggle, dots
    const KINDS = [[0, 34], [1, 13], [2, 12], [3, 17], [4, 10], [5, 7], [6, 7]];
    const total = KINDS.reduce((s, k) => s + k[1], 0);
    const pick = () => {
      let r = Math.random() * total;
      for (const [c, w] of KINDS) { if ((r -= w) < 0) return c; }
      return 0;
    };
    const canvas = document.createElement('canvas');
    canvas.className = 'lwa-trail';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.append(canvas);
    document.body.classList.add('lwa-has-trail');
    // the menu is a modal <dialog> (drawn above everything), so the trail
    // moves into it while it is open
    const menu = document.querySelector('dialog.lc-menu');
    if (menu) {
      new MutationObserver(() => {
        const host = menu.open ? menu : document.body;
        if (canvas.parentNode !== host) host.append(canvas);
      }).observe(menu, { attributes: true, attributeFilter: ['open'] });
    }
    const ctx = canvas.getContext('2d');
    let dpr = 1, W = 0, H = 0;
    function size() {
      dpr = Math.min(2, devicePixelRatio || 1);
      W = innerWidth; H = innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    size();
    addEventListener('resize', size);
    const bag = document.querySelector('.lc-nav .lc-bag-link');
    let parts = [], last = null, lastT = 0, running = false;
    onPoint((x, y, kind) => {
      const now = performance.now();
      if (!last) { last = { x, y }; lastT = now; return; }
      const dx = x - last.x, dy = y - last.y, dist = Math.hypot(dx, dy);
      const step = kind === 'touch' ? 34 : 30;
      if (dist < step) return;
      if (dist > 400) { last = { x, y }; lastT = now; return; } // a jump, not a stroke
      const speed = clamp(dist / Math.max(8, now - lastT), 0, 3); // px per ms
      last = { x, y }; lastT = now;
      let cell = pick();
      if (bag) {
        const b = bag.getBoundingClientRect();
        if (Math.hypot(x - (b.left + b.width / 2), y - (b.top + b.height / 2)) < 190) cell = 0;
      }
      const big = cell === 0 ? 1.12 : cell === 5 ? 1.2 : 1;
      parts.push({
        x, y, cell,
        size: ((kind === 'touch' ? 11 : 12) + Math.random() * 5 + speed * 2) * big,
        rot: Math.atan2(dy, dx) * 0.25 + (Math.random() - 0.5) * 0.9,
        spin: (Math.random() - 0.5) * 0.004,
        vx: -dx / dist * 0.02 + (Math.random() - 0.5) * 0.03,
        vy: -0.02 - Math.random() * 0.02,
        born: now,
        life: 900 + Math.random() * 250,
      });
      if (parts.length > 42) parts = parts.slice(-42);
      if (!running) { running = true; requestAnimationFrame(draw); }
    });
    function draw(now) {
      ctx.clearRect(0, 0, W, H);
      parts = parts.filter((p) => now - p.born < p.life);
      ctx.fillStyle = '#000';
      ctx.strokeStyle = '#fff';
      ctx.lineJoin = 'round';
      {
        for (const p of parts) {
          const age = now - p.born, k = age / p.life;
          // pop in, then sink and fade
          const pop = k < 0.12 ? 0.6 + (k / 0.12) * 0.45 : k < 0.22 ? 1.05 - ((k - 0.12) / 0.1) * 0.05 : 1 - (k - 0.22) * 0.2;
          const alpha = k < 0.55 ? 0.85 : 0.85 * (1 - (k - 0.55) / 0.45);
          const x = p.x + p.vx * age, y = p.y + p.vy * age + 0.00006 * age * age;
          const s = p.size * pop;
          ctx.save();
          ctx.globalAlpha = Math.max(0, alpha);
          ctx.translate(x, y);
          ctx.rotate(p.rot + p.spin * age);
          ctx.scale(s / 100, s / 100);
          // thin white edge first, so the motif reads on top of the drawings
          ctx.lineWidth = 260 / s;
          ctx.stroke(SHAPES[p.cell]);
          ctx.fill(SHAPES[p.cell], 'evenodd');
          ctx.restore();
        }
      }
      if (parts.length) requestAnimationFrame(draw);
      else { running = false; ctx.clearRect(0, 0, W, H); }
    }
  }

  /* ------------------------------------------------------------------ */
  /* BOOK LOGO: at the bottom of BOOK the LUGAZI logo glides to the     */
  /* middle of the screen and grows to cover almost the whole width,     */
  /* while the pages behind it fade away.                                */
  /* ------------------------------------------------------------------ */
  function initBookLogo() {
    const link = document.querySelector('.lc-nav .lc-return');
    const book = document.querySelector('.lw-book');
    const brand = link?.querySelector('.lc-brand') || link;
    if (!link || !book || link.dataset.lwaBookLogo) return;
    link.dataset.lwaBookLogo = '1';
    let sx = 0, y = 0, scale = 1, velocity = 0, tx = 0, ty = 0, ts = 1, last = 0, raf = 0;
    function plan() {
      if (reduced.matches || document.body.classList.contains('lugazi-menu-open')) { tx = ty = 0; ts = 1; }
      else {
        const w = innerWidth, h = innerHeight;
        const p = clamp((h * 1.45 - book.getBoundingClientRect().bottom) / (h * 0.55), 0, 1);
        const e = p * p * (3 - 2 * p);
        // where the logo normally sits (measured without our transform)
        const saved = link.style.transform;
        link.style.transform = 'none';
        const L = link.getBoundingClientRect(), B = brand.getBoundingClientRect();
        link.style.transform = saved;
        // target: middle of the screen, ~95 % of the screen width
        const s = 1 + ((w * 0.95) / Math.max(1, B.width) - 1) * e;
        const ox = L.left + L.width / 2, oy = L.top + L.height / 2;       // transform origin
        const cx = B.left + B.width / 2, cy = B.top + B.height / 2;       // logo centre
        // centre after translate(T) scale(s) around the origin = O + T + s(C - O)
        const fx = cx + (w / 2 - cx) * e, fy = cy + (h / 2 - cy) * e;
        tx = fx - ox - s * (cx - ox);
        ty = fy - oy - s * (cy - oy);
        ts = s;
        // the pages behind fade away as it lands
        document.body.style.setProperty('--lwa-end', e.toFixed(3));
      }
      if (!raf) raf = requestAnimationFrame(frame);
    }
    function frame(time) {
      raf = 0;
      const dt = Math.min(0.04, (time - (last || time - 16)) / 1000);
      last = time;
      const k = fine.matches ? 1 - Math.exp(-dt * 18) : 1;
      const px = sx;
      sx += (tx - sx) * k; y += (ty - y) * k; scale += (ts - scale) * k;
      velocity += (clamp((sx - px) * 0.08, -2, 2) - velocity) * (1 - Math.exp(-dt * 8));
      const moving = Math.abs(sx) > 0.2 || Math.abs(y) > 0.2 || Math.abs(scale - 1) > 0.002;
      link.style.transform = moving ? `translate(${sx}px,${y}px) rotate(${velocity}deg) scale(${scale})` : '';
      link.dataset.logoState = ts > 1.001 ? 'landing' : 'still';
      // the big logo must not catch the wheel / finger, or you cannot scroll back up
      link.style.pointerEvents = scale > 1.3 ? 'none' : '';
      if (Math.abs(tx - sx) > 0.2 || Math.abs(ty - y) > 0.2 || Math.abs(ts - scale) > 0.002 || Math.abs(velocity) > 0.03) raf = requestAnimationFrame(frame);
      else last = 0;
    }
    document.addEventListener('scroll', plan, { passive: true, capture: true });
    addEventListener('resize', plan, { passive: true });
    plan();
  }

  /* ------------------------------------------------------------------ */
  /* CURSOR: a small hand-drawn star is the mouse pointer (desktop).     */
  /* Its centre sits on the mouse; over links and buttons it turns a     */
  /* little. Replaces the small sun cursor of lugazi-world.js.           */
  /* ------------------------------------------------------------------ */
  function initCursor() {
    if (!fine.matches || reduced.matches || document.querySelector('.lwa-cursor')) return;
    // the hand-drawn star from the drawings (same as in the trail); the
    // mouse point is its centre
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('class', 'lwa-cursor');
    el.setAttribute('viewBox', '-55 -55 110 110');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<path fill="#000" fill-rule="evenodd" stroke="#fff" stroke-width="9" stroke-linejoin="round" paint-order="stroke" d="M20 34C19 33 16 32 15 30C13 29 11 27 9 25C7 24 4 22 2 21C-0 19 -2 18 -3 18C-4 18 -9 21 -13 23C-14 24 -16 25 -16 25C-16 25 -18 26 -20 28C-28 33 -32 36 -34 34C-36 33 -35 32 -32 29C-30 27 -28 25 -27 23C-26 22 -24 19 -23 18C-21 16 -20 14 -20 14C-19 13 -15 7 -16 7C-16 6 -23 -1 -24 -1C-24 -1 -26 -2 -28 -4C-30 -6 -32 -8 -33 -9C-34 -9 -35 -10 -36 -11C-37 -12 -39 -14 -41 -15C-43 -17 -45 -19 -45 -19C-45 -20 -46 -21 -46 -22C-46 -22 -45 -25 -44 -26C-43 -27 -43 -27 -42 -26C-42 -25 -41 -25 -40 -25C-40 -25 -39 -25 -39 -25C-39 -24 -33 -21 -32 -21C-31 -21 -30 -21 -30 -20C-29 -20 -28 -20 -27 -20C-26 -20 -25 -19 -24 -19C-24 -19 -22 -18 -21 -18C-20 -18 -18 -17 -16 -17C-15 -16 -12 -16 -10 -16C-9 -15 -7 -15 -7 -15C-6 -14 -3 -17 -3 -18C-3 -18 -2 -20 -1 -23C0 -25 2 -28 2 -29C2 -31 2 -32 4 -34C6 -36 8 -37 9 -35C9 -35 9 -34 10 -35C11 -35 12 -31 12 -29C12 -29 13 -27 13 -26C14 -25 14 -24 14 -23C14 -23 14 -21 15 -20C15 -19 16 -18 16 -17C17 -12 21 -11 34 -11C44 -11 44 -11 46 -8C47 -7 47 -7 45 -5C44 -4 43 -4 42 -4C42 -3 40 -3 40 -3C39 -2 38 -2 37 -2C37 -2 36 -2 36 -2C36 -1 35 -1 35 -1C33 -1 21 5 21 6C21 8 21 11 22 12C22 12 22 14 23 15C23 18 24 20 25 24C26 25 26 27 26 27C26 28 27 28 27 28C29 29 26 35 22 36C22 36 21 35 20 34ZM13 15C13 15 13 13 13 12L12 10 10 12L8 13 10 15C12 17 14 17 13 15ZM2 7C3 6 5 5 6 5C10 2 10 2 10 -1C10 -3 10 -4 9 -4C8 -6 2 -6 1 -6C-1 -5 -4 2 -4 4C-4 7 0 9 2 7ZM-11 -3C-10 -3 -10 -4 -9 -5C-9 -7 -9 -7 -17 -9C-19 -9 -19 -8 -15 -5C-12 -1 -12 -1 -11 -3Z"/>';
    document.body.append(el);
    document.body.classList.add('lwa-has-cursor');
    // follow the menu into its modal <dialog> so the arrow stays on top
    const menu = document.querySelector('dialog.lc-menu');
    if (menu) {
      new MutationObserver(() => {
        const host = menu.open ? menu : document.body;
        if (el.parentNode !== host) host.append(el);
      }).observe(menu, { attributes: true, attributeFilter: ['open'] });
    }
    let x = -100, y = -100, tilt = 0, want = 0, raf = 0;
    function draw() {
      raf = 0;
      tilt += (want - tilt) * 0.25;
      el.style.transform = `translate(${x}px,${y}px) rotate(${tilt.toFixed(2)}deg)`;
      if (Math.abs(want - tilt) > 0.1) raf = requestAnimationFrame(draw);
    }
    addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      x = e.clientX; y = e.clientY;
      const t = e.target instanceof Element ? e.target : null;
      const form = t?.closest('input, textarea, select, [contenteditable="true"]');
      el.classList.toggle('is-off', !!form);
      el.classList.add('is-on');
      want = t?.closest('a, button, [role="button"], label, summary, .lw-collage-page, .lc-cat') ? 36 : 0; // the star turns over links
      if (!raf) raf = requestAnimationFrame(draw);
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => el.classList.remove('is-on'));
    addEventListener('pointerdown', () => el.classList.add('is-down'), { passive: true });
    addEventListener('pointerup', () => el.classList.remove('is-down'), { passive: true });
  }

  /* Start when lugazi-world.js has built the page */
  function start() {
    initCat();
    initHover();
    initTrail();
    initCursor();
    if (document.body.dataset.lugaziView === 'home') { initFaceBlink(); initTreeFace(); initFigure(); initTouchEyes(); }
    if (document.body.dataset.lugaziView !== 'book') return;
    initBookLogo();
    if (document.querySelector('.lw-collage-spread')) initBook();
    else {
      const mo = new MutationObserver(() => {
        if (document.querySelector('.lw-collage-spread')) { mo.disconnect(); initBook(); }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
